<template>
  <div
    class="event-card"
    :class="{ 'sale-unavailable': !!saleStatus }"
    :aria-disabled="!!saleStatus"
    @click="handleOpenEvent"
  >
    <div class="card-wrapper">
      <div class="card-content">
        <div class="event-date">
          <div class="event-month">{{ workflowLabel(workflowDate(dateAndTime, 'MMM')) }}</div>
          <div class="event-day">{{ workflowLabel(workflowDate(dateAndTime, 'DD')) }}</div>
        </div>
        <div class="event-content">
          <div class="event-title">{{ workflowLabel(title) }}</div>
          <div class="event-caption">
            {{ workflowLabel(workflowDate(dateAndTime, 'MMM')) }} {{ workflowLabel(workflowDate(dateAndTime, 'DD')) }},
            {{ workflowLabel(workflowDate(dateAndTime, 'YYYY')) }} {{ adminText('workflowsAll.at_27e90df', 'at') }} {{ workflowLabel(workflowDate(dateAndTime, 'HH')) }}:{{
              workflowLabel(workflowDate(dateAndTime, 'mm'))
            }}
            @
            {{ workflowLabel(location.venue) }}
          </div>
          <div v-if="saleStatus" class="sale-status">{{ saleStatusLabel }}</div>
          <div v-else class="tickets-data">
            <div class="available-tickets-number" translate="no">{{ workflowLabel(availableQuantity) }}</div>
            <div class="total-tickets-number" translate="no">/{{ workflowLabel(initialQuantity) }} {{ adminText('workflowsAll.available_7b231a5', 'available') }}</div>
          </div>
        </div>
      </div>
      <img :alt="adminText('workflowsAll.event_type_d5b2b91', 'event-type')" :src="imgSrc" class="event-type-img">
    </div>
    <div v-if="cover" class="event-photo">
      <img :src="cover">
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import moment from 'moment'
import { formatDate } from '@/utils'
import { EventTypeImgSrc, OrganizationType } from '@/types'
import { getEventSaleStatus } from '@/utils/event-sale'
import { countSellableSeats } from '@/utils/floor-plan-quantity'

export default {
  mixins: [WorkflowDisplay],
  name: 'event-card',
  props: {
    event: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dateAndTime: moment(this.event.from).locale('en'),
      location: this.event.location,
      availableQuantity: 0,
      initialQuantity: 0,
      EventTypeImgSrc,
      title: this.event.title,
      cover: this.event.cover,
      imgSrc: this.event.extraModules.floorPlan
        ? EventTypeImgSrc.FLOOR_PLAN
        : EventTypeImgSrc.REGULAR,
      formatDate,
    }
  },

  computed: {
    saleStatusLabel() {
      switch (this.saleStatus) {
      case 'Canceled':
        return this.adminText('workflowsAll.canceled_d96e4ff', 'Canceled')
      case 'Sold out':
        return this.adminText('workflowsAll.sold_out_02ff659', 'Sold out')
      case 'Sales closed':
        return this.adminText('workflowsAll.sales_closed_49ddcd4', 'Sales closed')
      default:
        return this.saleStatus
      }
    },

    saleStatus() {
      return getEventSaleStatus(this.event)
    },
  },
  methods: {
    handleOpenEvent() {
      if (this.saleStatus) return
      this.goTo(this.routes.ticketSeller.purchase, { eventId: this.event.id })
    },
  },

  mounted() {
    if (this.event.organization.type === OrganizationType.THEATER) {
      this.initialQuantity = this.event.audienceCapacity
      this.availableQuantity = this.event.numOfRemainingTickets

      return
    }

    for (let orderItem of this.event.orderItems) {
      const initialQuantity = Number(orderItem.initialQuantity)
      const quantity = Number(orderItem.quantity)
      const sectionSeatsLength = countSellableSeats(orderItem.sectionSeats)
      if (!this.event.extraModules.floorPlan) {
        this.initialQuantity += initialQuantity
        this.availableQuantity += quantity
        continue
      }
      if (orderItem.itemMeta.parter) {
        this.initialQuantity += initialQuantity
        this.availableQuantity += quantity
      } else if (orderItem.itemMeta.table) {
        this.initialQuantity += orderItem.itemMeta.max * sectionSeatsLength
        this.availableQuantity +=
          orderItem.itemMeta.max * sectionSeatsLength - (initialQuantity - quantity)
      } else {
        this.initialQuantity += sectionSeatsLength
        this.availableQuantity += sectionSeatsLength - (initialQuantity - quantity)
      }
    }
  },
}
</script>

<style scoped>
.event-card {
  box-shadow: none;
  display: flex;
  background-color: #f5f5f5;
  width: 100%;
  border-radius: 1.5rem;
  justify-content: space-between;
  cursor: pointer;
  font-family: Raleway, 'sans-serif';
  font-feature-settings: 'pnum' on;
  font-feature-settings: 'lnum' on;
}

.event-title {
  color: #0277bd;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.03em;
  padding: 0;
}

.sale-unavailable {
  cursor: default;
}

.sale-status {
  color: #db384c;
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 120%;
  margin-top: 0.5rem;
}

.card-content {
  display: flex;
  align-items: flex-start;
  width: 100%;
}

.event-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
}

.tickets-data {
  display: flex;
  align-items: baseline;
}

.v-card__text {
  padding: 0;
  width: unset;
}

.event-caption {
  color: #333333;
  font-family: 'Source sans pro';
  font-weight: 400;
  line-height: 120%;
  font-size: 0.875rem;
  margin: 0.5rem 0;
}

.available-tickets-number {
  font-size: 2rem;
  line-height: 120%;
  color: #0277bd;
  font-weight: 800;
  letter-spacing: -0.015em;
  font-feature-settings: 'pnum' on, 'lnum' on;
}

.total-tickets-number {
  color: #666666;
  font-size: 1rem;
  line-height: 120%;
  font-weight: 600;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
}

.event-date {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 1rem 0 1rem;
}

.event-month {
  font-weight: 700;
  font-size: 1rem !important;
  text-align: center;
  font-family: Raleway, sans-serif;
  text-transform: uppercase;
  line-height: 120%;
  letter-spacing: -0.015em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}

.event-day {
  font-weight: 600;
  font-size: 1.5rem;
  text-align: center;
  font-family: Raleway, sans-serif;
  line-height: 100%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
}

.event-photo {
  aspect-ratio: 16/9;
  max-width: 213px;
  border-radius: 0 1.5rem 1.5rem 0;
  overflow: hidden;
}

.event-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@media (max-width: 576px) {
  .event-card {
    display: flex;
    flex-direction: column-reverse;
    max-height: fit-content;
  }

  .event-content {
    min-height: 100%;
  }

  .event-photo {
    max-width: unset;
    border-radius: 1.5rem 1.5rem 0 0;
  }
}
.card-wrapper {
  display: flex;
  justify-content: space-between;
  margin: 1rem 0.75rem 0.75rem 0;
  width: 100%;
}
.event-type-img {
  height: 25px;
  width: 25px;
}
</style>
